// Ship Speed (design doc 16.28): matters only *between* encounters, not
// within them (no simulated in-encounter distance, per the command-driven
// premise) -- specifically per-hop travel time and flee/disengage success
// chance (combatResolutionLoop.ts's attemptFlee).
//
// The role-speed-multiplier and size-speed-multiplier VALUES themselves
// aren't tabulated anywhere in the doc for all 6 roles x 4 sizes -- only
// two worked examples exist (Scout: role 1.5x, size 1.2x; Miner: role
// 0.7x, size 1.2x). This module takes both multipliers as parameters
// rather than inventing the missing table.
import { bonusMultiplier } from "./captainSkills";

// Piloting skill stacks multiplicatively (16.31): final speed = role_mult x
// size_mult x (1 + skill_bonus).
export function combinedSpeedMultiplier(
  roleMultiplier: number,
  sizeMultiplier: number,
  pilotingSkillXp = 0
): number {
  return roleMultiplier * sizeMultiplier * bonusMultiplier(pilotingSkillXp);
}

// Hop time = 60s base / combined speed multiplier (16.28's proposed formula).
export function hopTimeSeconds(
  roleMultiplier: number,
  sizeMultiplier: number,
  pilotingSkillXp = 0
): number {
  return 60 / combinedSpeedMultiplier(roleMultiplier, sizeMultiplier, pilotingSkillXp);
}

// Par time for a multi-hop route -- needed for Hauling/VIP's "time bonus"
// (16.26/16.27). The bonus MULTIPLIER curve itself (comparing actual vs.
// par time into a ~0.8x-1.2x reward multiplier) is explicitly flagged in
// 16.26 as "exact curve TBD," blocked on this very hop-time stat not
// existing yet -- now that it does, the par-time half is computable, but
// the bonus curve is still not locked, so it isn't invented here.
export function routeParTimeSeconds(
  hops: number,
  roleMultiplier: number,
  sizeMultiplier: number,
  pilotingSkillXp = 0
): number {
  return hops * hopTimeSeconds(roleMultiplier, sizeMultiplier, pilotingSkillXp);
}

// Hauling/VIP time-bonus curve (16.56, LOCKED interview pass): linear,
// tied directly to `routeParTimeSeconds` above. Finishing at or under par
// pays the full 1.2x; finishing at 2x par or worse floors at 0.8x;
// straight-line interpolation between. Applies identically to Hauling
// (16.26) and VIP Transport (16.27).
export function timeBonusMultiplier(actualTimeSeconds: number, parTimeSeconds: number): number {
  if (parTimeSeconds <= 0) {
    return 1.2;
  }
  return Math.max(0.8, Math.min(1.2, 1.2 - 0.4 * (actualTimeSeconds / parTimeSeconds - 1)));
}
